import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import {
  Sparkles, BookOpen, FileText, ClipboardCheck, RefreshCw,
  Check, Clock, Users, ShieldCheck, Zap, Star
} from "lucide-react";

const HERO_IMG = "https://customer-assets-jt897jd0.emergentagent.net/job_teacher-ai-hub-13/artifacts/gmia4eto_imagem%201.webp";
const LAPTOP_IMG = "https://customer-assets-jt897jd0.emergentagent.net/job_teacher-ai-hub-13/artifacts/ucpcanim_imagem%202.webp";
const TECH_IMG = "https://customer-assets-jt897jd0.emergentagent.net/job_teacher-ai-hub-13/artifacts/f764s1l7_imagem%203.webp";
const DEMO_VIDEO = "https://customer-assets-jt897jd0.emergentagent.net/job_teacher-ai-hub-13/artifacts/f57ax375_video%201.MOV";

const TOOLS = [
  { icon: BookOpen, title: "Plano de Aula", desc: "Estruturado, com objetivos, metodologia e avaliação." },
  { icon: FileText, title: "Atividades", desc: "Múltipla escolha, discursivas, problemas — com gabarito." },
  { icon: ClipboardCheck, title: "Avaliações", desc: "Cabeçalho pronto, questões e gabarito separado." },
  { icon: RefreshCw, title: "Adaptar Conteúdo", desc: "Simplifique ou aprofunde qualquer material em segundos." },
];

const BENEFITS = [
  { icon: Clock, title: "Economize horas por semana", desc: "Do planejamento à avaliação em minutos, não em noites." },
  { icon: Zap, title: "Personalizado por série", desc: "Da Educação Infantil ao 3º ano do Ensino Médio." },
  { icon: ShieldCheck, title: "Sem invenções de BNCC", desc: "Conteúdo confiável, sem códigos ou fontes inventadas." },
  { icon: Users, title: "Feito para professores reais", desc: "Interface simples: preencha, gere, edite, baixe em PDF." },
];

const DISCIPLINAS = ["Matemática", "Língua Portuguesa", "História", "Geografia", "Ciências", "Inglês", "Artes", "Educação Física", "Ensino Religioso"];

const FAQ = [
  { q: "É realmente grátis para começar?", a: "Sim. Todo novo professor recebe 5 gerações gratuitas para experimentar todas as ferramentas antes de decidir assinar." },
  { q: "Posso cancelar quando quiser?", a: "Sim. A assinatura Premium é mensal e você pode cancelar a qualquer momento pela sua área de conta." },
  { q: "A IA inventa conteúdo?", a: "Nossos prompts foram projetados para evitar invenção de códigos da BNCC ou referências específicas. O professor sempre revisa antes de aplicar." },
  { q: "Funciona no celular?", a: "Sim, o AulaPronta funciona no computador, tablet e celular." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-slate-200">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
          <Logo />
          <div className="flex items-center gap-2">
            <Link to="/login">
              <Button variant="ghost" className="rounded-full text-slate-700" data-testid="header-login">Entrar</Button>
            </Link>
            <Link to="/register">
              <Button className="rounded-full bg-[#1F3A8A] hover:bg-[#15265b]" data-testid="header-register">Começar grátis</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: "#1F3A8A" }}>
        <div className="absolute inset-0 opacity-30 pointer-events-none"
             style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #8B5CF6 0%, transparent 40%), radial-gradient(circle at 80% 70%, #3B82F6 0%, transparent 40%)" }}/>
        <div className="relative max-w-7xl mx-auto px-6 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold text-[#FBBF24] mb-6">
              <Sparkles size={14} /> Do planejamento à avaliação em minutos
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.05] tracking-tight">
              Prepare suas aulas <span className="text-[#FBBF24]">em minutos, não em horas.</span>
            </h1>
            <p className="mt-6 text-lg text-white/80 max-w-xl">
              Plano de Aula <span className="font-bold text-white">+ Aula Completa</span> com IA, prontos para usar em minutos.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link to="/register">
                <Button size="lg" className="rounded-full bg-[#FBBF24] hover:brightness-110 text-[#0F172A] font-bold px-7 py-6 text-base shadow-[0_10px_30px_rgba(251,191,36,0.35)]"
                  data-testid="hero-cta">
                  Começar grátis
                </Button>
              </Link>
              <span className="text-white/80 text-sm">🎁 5 gerações gratuitas para você experimentar.</span>
            </div>
          </div>
          <div className="relative">
            <div className="rounded-3xl overflow-hidden shadow-2xl ring-1 ring-white/10">
              <img src={HERO_IMG} alt="AulaPronta em uso no MacBook" className="w-full h-[420px] object-cover object-top" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-4 shadow-xl border border-slate-100 hidden md:block">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#8B5CF6]/15 flex items-center justify-center">
                  <Sparkles className="text-[#8B5CF6]" size={20} />
                </div>
                <div>
                  <div className="text-xs uppercase text-slate-500 font-semibold tracking-widest">IA pedagógica</div>
                  <div className="font-heading font-bold text-slate-900">Plano em minutos</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video em mockup celular */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#8B5CF6] mb-3">Veja funcionando</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900">O AulaPronta no seu bolso.</h2>
            <p className="mt-4 text-slate-600 text-lg max-w-lg">Do intervalo até o ônibus de volta pra casa: prepare materiais direto no celular.</p>
            <Link to="/register">
              <Button size="lg" className="mt-8 rounded-full bg-[#1F3A8A] hover:bg-[#15265b] px-7 py-6 text-base" data-testid="video-cta">
                Crie sua primeira aula
              </Button>
            </Link>
          </div>
          <div className="flex justify-center">
            <div className="relative" style={{ width: 300, maxWidth: "80vw" }}>
              {/* Phone frame */}
              <div className="relative rounded-[44px] bg-slate-900 p-3 shadow-[0_30px_60px_rgba(31,58,138,0.35)]" style={{ aspectRatio: "9/19.5" }}>
                <div className="absolute top-3 left-1/2 -translate-x-1/2 w-28 h-6 bg-slate-900 rounded-b-2xl z-10" />
                <div className="rounded-[32px] overflow-hidden bg-black h-full">
                  <video
                    src={DEMO_VIDEO}
                    autoPlay muted loop playsInline controls
                    className="w-full h-full object-cover"
                    data-testid="demo-video"
                  />
                </div>
              </div>
              <div className="absolute -right-6 -bottom-2 w-1 h-20 bg-slate-900 rounded-r-xl hidden sm:block"/>
            </div>
          </div>
        </div>
      </section>

      {/* Como funciona */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="max-w-3xl">
          <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#8B5CF6] mb-3">Como funciona</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900">Três passos, e pronto.</h2>
        </div>
        <div className="mt-10 grid md:grid-cols-3 gap-6">
          {[
            { n: "01", t: "Escolha a ferramenta", d: "Plano de aula, atividade, avaliação ou adaptar conteúdo." },
            { n: "02", t: "Preencha os dados", d: "Disciplina, série, tema e o que mais quiser detalhar." },
            { n: "03", t: "Gere, edite e baixe", d: "A IA cria, você ajusta, salva e baixa em PDF." },
          ].map((s) => (
            <div key={s.n} className="bg-white p-8 rounded-2xl border border-slate-200 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <div className="text-4xl font-heading font-extrabold text-[#8B5CF6]">{s.n}</div>
              <h3 className="mt-3 font-heading text-xl font-bold text-slate-900">{s.t}</h3>
              <p className="mt-2 text-slate-600 text-sm leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Ferramentas (Bento) */}
      <section className="bg-[#F8FAFC] py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#8B5CF6] mb-3">Ferramentas</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900">Quatro ferramentas para o seu dia a dia.</h2>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {TOOLS.map((t, i) => (
              <div key={t.title} className="bg-white p-6 rounded-2xl border border-slate-200 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:border-[#8B5CF6]/50 hover:-translate-y-1 transition-transform">
                <div className={`p-3 rounded-xl w-fit ${i % 2 ? "bg-[#8B5CF6]/10 text-[#8B5CF6]" : "bg-[#1F3A8A]/10 text-[#1F3A8A]"}`}>
                  <t.icon size={22} />
                </div>
                <h3 className="mt-4 font-heading text-lg font-bold text-slate-900">{t.title}</h3>
                <p className="mt-1.5 text-sm text-slate-600">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefícios com imagem */}
      <section className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-12 items-center">
        <img src={LAPTOP_IMG} alt="Exemplo de plano de aula gerado" className="rounded-3xl w-full h-auto object-contain shadow-xl bg-slate-50"/>
        <div>
          <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#8B5CF6] mb-3">Resultado gerado</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900">Do formulário ao material pronto.</h2>
          <p className="mt-4 text-slate-600">Você recebe o material estruturado, editável e pronto para baixar em PDF — sem marca d'água, pronto para levar para a sala de aula.</p>
          <div className="mt-8 space-y-5">
            {BENEFITS.map(b => (
              <div key={b.title} className="flex items-start gap-4">
                <div className="p-2.5 rounded-xl bg-[#1F3A8A]/10 text-[#1F3A8A] flex-shrink-0"><b.icon size={20}/></div>
                <div>
                  <h4 className="font-heading font-bold text-slate-900">{b.title}</h4>
                  <p className="text-slate-600 text-sm">{b.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <Link to="/register">
            <Button size="lg" className="mt-8 rounded-full bg-[#1F3A8A] hover:bg-[#15265b] px-7 py-6 text-base" data-testid="benefits-cta">
              Experimentar gratuitamente
            </Button>
          </Link>
        </div>
      </section>

      {/* Disciplinas */}
      <section className="bg-[#F8FAFC] py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#8B5CF6] mb-3">Todas as disciplinas</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900">Não é só matemática.</h2>
            <p className="mt-3 text-slate-600">O AulaPronta cobre todas as disciplinas da educação básica.</p>
          </div>
          <div className="mt-10 flex flex-wrap gap-3">
            {DISCIPLINAS.map(d => (
              <span key={d} className="px-4 py-2 rounded-full bg-white border border-slate-200 text-sm font-medium text-slate-700 shadow-sm">{d}</span>
            ))}
          </div>
        </div>
      </section>

      {/* IA */}
      <section className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#8B5CF6] mb-3">Atividades e avaliações</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900">Materiais prontos para a sala de aula.</h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            Muito além do plano de aula: gere listas de exercícios, provas, atividades práticas e adapte materiais para diferentes turmas. Tudo com gabarito automático e pronto para imprimir — sem marca d'água.
          </p>
          <ul className="mt-6 space-y-2.5 text-sm">
            {["Múltipla escolha, verdadeiro/falso, discursivas e problemas","Gabarito separado só para o professor","Cabeçalho pronto (nome, turma, data)","PDF limpo — sem logo, sem propaganda"].map(x => (
              <li key={x} className="flex items-center gap-2 text-slate-700"><Check className="text-[#8B5CF6]" size={16}/> {x}</li>
            ))}
          </ul>
          <Link to="/register">
            <Button size="lg" className="mt-6 rounded-full bg-[#8B5CF6] hover:bg-[#7c4ef4] px-7 py-5 text-base" data-testid="materials-cta">
              Crie sua primeira atividade
            </Button>
          </Link>
        </div>
        <img src={TECH_IMG} alt="Ferramentas por disciplina" className="rounded-3xl w-full h-auto object-contain shadow-xl bg-slate-50"/>
      </section>

      {/* Pricing */}
      <section className="bg-[#F8FAFC] py-20">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#8B5CF6] mb-3">Plano</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900">Simples. Um preço, tudo liberado.</h2>
        </div>
        <div className="mt-10 max-w-md mx-auto px-6">
          <div className="relative bg-white rounded-3xl p-8 border-2 border-[#8B5CF6] shadow-[0_20px_60px_rgba(139,92,246,0.2)]">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#FBBF24] text-[#0F172A] text-xs font-bold px-4 py-1.5 rounded-full flex items-center gap-1">
              <Star size={12} fill="currentColor"/> POPULAR
            </div>
            <h3 className="font-heading text-2xl font-extrabold text-slate-900">AulaPronta Premium</h3>
            <div className="mt-3 flex items-baseline gap-1">
              <span className="text-5xl font-heading font-extrabold text-[#1F3A8A]">R$ 19,90</span>
              <span className="text-slate-500">/mês</span>
            </div>
            <p className="mt-2 text-slate-600 text-sm">Tenha acesso completo às ferramentas do AulaPronta.</p>
            <ul className="mt-6 space-y-2.5 text-sm">
              {["Plano de aula ilimitado (limite mensal alto)","Todas as atividades e avaliações","Adaptação de conteúdo","Todas as disciplinas","Histórico completo","Exportação em PDF"].map(x => (
                <li key={x} className="flex items-center gap-2 text-slate-700"><Check className="text-[#8B5CF6]" size={16}/> {x}</li>
              ))}
            </ul>
            <Link to="/register">
              <Button className="w-full mt-7 rounded-full bg-[#1F3A8A] hover:bg-[#15265b] py-6 text-base" data-testid="pricing-cta">
                Começar grátis
              </Button>
            </Link>
            <p className="text-center text-xs text-slate-500 mt-3">5 gerações grátis. Sem cartão para começar.</p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-6 py-20">
        <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#8B5CF6] mb-3">Dúvidas frequentes</div>
        <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900">Perguntas rápidas.</h2>
        <div className="mt-8 space-y-4">
          {FAQ.map(f => (
            <details key={f.q} className="group bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
              <summary className="cursor-pointer font-heading font-semibold text-slate-900 list-none flex items-center justify-between">
                {f.q}
                <span className="text-[#8B5CF6] group-open:rotate-45 transition-transform text-xl">+</span>
              </summary>
              <p className="mt-3 text-slate-600 text-sm leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-6" style={{ background: "linear-gradient(135deg, #1F3A8A 0%, #8B5CF6 100%)" }}>
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-3xl sm:text-5xl font-extrabold text-white leading-tight">
            Comece agora. Sem cartão. Sem enrolação.
          </h2>
          <p className="mt-4 text-white/80">Junte-se aos professores que já economizam horas com o AulaPronta.</p>
          <Link to="/register">
            <Button size="lg" className="mt-8 rounded-full bg-[#FBBF24] hover:brightness-110 text-[#0F172A] font-bold px-8 py-6 text-base shadow-xl"
              data-testid="final-cta">
              Começar grátis
            </Button>
          </Link>
        </div>
      </section>

      <footer className="border-t border-slate-200 py-10 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid gap-6 md:grid-cols-3 items-start">
          <div>
            <Logo size="sm" />
            <p className="mt-3 text-sm text-slate-500 max-w-xs">Plano de Aula + Aula Completa com IA para professores da educação básica.</p>
          </div>
          <div className="text-sm text-slate-600 space-y-2">
            <div className="font-heading font-semibold text-slate-900">Institucional</div>
            <Link to="/termos" className="block hover:text-[#1F3A8A]" data-testid="footer-terms">Termos de Uso</Link>
            <Link to="/privacidade" className="block hover:text-[#1F3A8A]" data-testid="footer-privacy">Política de Privacidade</Link>
          </div>
          <div className="text-sm text-slate-600 space-y-2">
            <div className="font-heading font-semibold text-slate-900">Ajuda</div>
            <Link to="/suporte" className="block hover:text-[#1F3A8A]" data-testid="footer-support">Ajuda e Suporte</Link>
            <a href="mailto:aulapronta01@gmail.com" className="block hover:text-[#1F3A8A]" data-testid="footer-email">aulapronta01@gmail.com</a>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 mt-8 pt-6 border-t border-slate-100 text-xs text-slate-500 text-center">
          © {new Date().getFullYear()} AulaPronta. Feito para professores.
        </div>
      </footer>
    </div>
  );
}
